import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Auth } from 'aws-amplify';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Switch,
    TextField,
    Alert,
    AlertTitle,
    Backdrop,
    CircularProgress,
    Snackbar
} from '@mui/material';
import * as Actions from '../../actions/actions';
import * as Service from '../../services/services';

const EDNSettings = ({ customers, ...props }) => {
    const [authToken, setAuthToken] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [availableDCs, setAvailableDCs] = useState([]);
    const [selectedDC, setSelectedDC] = useState(null);

    // Settings state
    const [ednEnabled, setEdnEnabled] = useState(false);
    const [sendTime, setSendTime] = useState('07:00');
    const [timezone, setTimezone] = useState('US/Central');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        Auth.currentSession().then(res => {
            const jwt = res.getAccessToken().getJwtToken();
            setAuthToken(jwt);
            localStorage.setItem('authToken', jwt);
            props.fetchCustomersWithDC();
        });
    }, []);

    const handleCustomerChange = (event) => {
        const customerId = event.target.value;
        const customer = customers.find(c => c.customer_id === customerId);
        setSelectedCustomer(customer);
        setAvailableDCs(customer ? customer.dcs || [] : []);
        setSelectedDC(null);
        setEdnEnabled(false);
        setSendTime('07:00');
        setTimezone('US/Central');
    };

    const handleDCChange = (event) => {
        const dcId = event.target.value;
        const dc = availableDCs.find(d => d.dc_id === dcId);
        setSelectedDC(dc);
        // Fetch EDN settings for this DC
        if (dc && authToken) {
            setLoading(true);
            Service.getEDNSettings(authToken, selectedCustomer.customer_id, dc.dc_id)
                .then(res => {
                    const data = res.data.results || {};
                    setEdnEnabled(data.edn_enabled || false);
                    const hour = String(data.send_hour ?? 7).padStart(2, '0');
                    const minute = String(data.send_minute ?? 0).padStart(2, '0');
                    setSendTime(`${hour}:${minute}`);
                    setTimezone(data.timezone || 'US/Central');
                })
                .catch(err => {
                    console.error('[EDN] Failed to load settings:', err);
                    setEdnEnabled(false);
                    setSendTime('07:00');
                    setTimezone('US/Central');
                })
                .finally(() => setLoading(false));
        }
    };

    const handleSave = () => {
        if (!selectedCustomer || !selectedDC || !authToken) return;
        setSaving(true);
        Service.saveEDNSettings(authToken, selectedCustomer.customer_id, selectedDC.dc_id, ednEnabled, sendTime, timezone)
            .then(() => {
                setSnackbar({ open: true, message: 'EDN settings saved successfully.', severity: 'success' });
            })
            .catch(err => {
                console.error('[EDN] Failed to save settings:', err);
                const msg = err.response?.data?.Error || 'Failed to save settings. Please try again.';
                setSnackbar({ open: true, message: msg, severity: 'error' });
            })
            .finally(() => setSaving(false));
    };

    const hasSelection = selectedCustomer && selectedDC;

    return (
        <div style={{ width: '100%' }}>
            <div>
                <h3>EDN Settings</h3>
                <p style={{ color: '#475569', fontSize: '14px', marginTop: '-8px' }}>
                    DC-level EDN enablement and local send time configuration.
                </p>

                {/* Customer / DC Selection */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                    <FormControl sx={{ width: 240 }} size="small">
                        <InputLabel>Customer</InputLabel>
                        <Select
                            value={selectedCustomer?.customer_id || ''}
                            onChange={handleCustomerChange}
                            label="Customer"
                        >
                            <MenuItem value=""><em>Select Customer</em></MenuItem>
                            {(customers || []).map((c) => (
                                <MenuItem key={c.customer_id} value={c.customer_id}>
                                    {c.customer_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ width: 240 }} size="small">
                        <InputLabel>Distribution Center</InputLabel>
                        <Select
                            value={selectedDC?.dc_id || ''}
                            onChange={handleDCChange}
                            label="Distribution Center"
                            disabled={!selectedCustomer}
                        >
                            <MenuItem value=""><em>Select DC</em></MenuItem>
                            {availableDCs.map((dc) => (
                                <MenuItem key={dc.dc_id} value={dc.dc_id}>
                                    {dc.dc_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                {/* Prompt to select */}
                {!hasSelection && (
                    <Alert severity="info" sx={{ marginBottom: '20px' }}>
                        <AlertTitle>Select Filters</AlertTitle>
                        Please select a Customer and Distribution Center to configure EDN settings.
                    </Alert>
                )}

                {/* Settings Panel */}
                {hasSelection && (
                    <div style={{
                        background: '#fff',
                        border: '1px solid rgba(15,23,42,.12)',
                        borderRadius: '16px',
                        boxShadow: '0 10px 30px rgba(2,6,23,.08)',
                        overflow: 'hidden'
                    }}>
                        {/* Panel Header */}
                        <div style={{
                            padding: '14px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(15,23,42,.08)',
                            background: 'rgba(15,23,42,.02)'
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '16px' }}>Settings</h3>
                                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#475569' }}>
                                    DC-level EDN enablement and local send time (time zone aware).
                                </p>
                            </div>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </Button>
                        </div>

                        {/* Panel Body */}
                        <div style={{ padding: '20px 16px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                                {/* Left Column */}
                                <div>
                                    <label style={{ fontSize: '12px', color: '#475569', display: 'block', marginBottom: '6px' }}>
                                        Distribution Center
                                    </label>
                                    <div style={{
                                        padding: '10px 12px',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(15,23,42,.12)',
                                        background: 'rgba(15,23,42,.02)',
                                        fontSize: '13px'
                                    }}>
                                        {selectedDC.dc_name} <span style={{ color: '#475569', fontSize: '12px' }}>({timezone})</span>
                                    </div>
                                    <p style={{ fontSize: '12px', color: '#475569', marginTop: '6px' }}>
                                        Send time is stored with DC time zone (DST-safe).
                                    </p>
                                </div>

                                {/* Right Column */}
                                <div>
                                    <label style={{ fontSize: '12px', color: '#475569', display: 'block', marginBottom: '6px' }}>
                                        EDN enabled for this DC
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                        <Switch
                                            checked={ednEnabled}
                                            onChange={(e) => setEdnEnabled(e.target.checked)}
                                        />
                                        <span style={{ fontWeight: 700, fontSize: '14px' }}>
                                            {ednEnabled ? 'On' : 'Off'}
                                        </span>
                                    </div>

                                    <label style={{ fontSize: '12px', color: '#475569', display: 'block', marginBottom: '6px' }}>
                                        Scheduled send time (DC local)
                                    </label>
                                    <TextField
                                        type="time"
                                        size="small"
                                        value={sendTime}
                                        onChange={(e) => setSendTime(e.target.value)}
                                        sx={{ width: 200 }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading */}
                <Backdrop
                    sx={{ color: '#000', backgroundColor: 'rgb(255,255,255,0.7)', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                >
                    <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    customers: state.edgePCs.customers || []
});

const mapDispatchToProps = (dispatch) => ({
    fetchCustomersWithDC: () => dispatch(Actions.LOAD_CUSTOMERS_WITH_DC())
});

export default connect(mapStateToProps, mapDispatchToProps)(EDNSettings);
